import React, { useContext } from "react";
import styled, { css } from "styled-components";

import { ConfirmModal, Modal } from "@examspark/common-ui";

import {
  getItemCount,
  getItemCountBefore,
  getItemCountInSection,
} from "../../utils/sections";
import Button from "../common/Button";

import { ExamContext } from "./context/examContext";
import ProgressBar from "./ProgressBar";

const ExamHeader = styled.div`
  /* background-color: #fff; */
  /* border: solid 1px #ccc; */
  /* box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.05); */
  padding: 0.1rem;
`;
const ExamHeaderProgress = styled.div`
  margin: 1rem 0;
`;
const ExamHeaderMain = styled.div`
  display: flex;
  align-items: center;
  height: 3.8rem;
  /* padding: 1rem 1rem; */
`;
const ExamHeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
`;
const ExamHeaderButtons = styled.div`
  margin-left: 1rem;
`;
const ExamTitle = styled.h1`
  font-size: 1.5em;
  margin: 0;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  display: block;
`;

const ExamHeaderStatus = styled.div`
  margin: 0 2rem;
`;

const SecondaryButton = styled(Button)`
  background-color: #e7e7e7;
  color: #333;
  display: flex;
  align-items: center;
  font-weight: bold;
  text-transform: uppercase;
  height: 3.8rem;
  transition: background-color 0.2s;

  ${(p) =>
    p.disabled
      ? css`
          color: grey;
          fill: grey;
        `
      : css`
          &:hover {
            background-color: #e0e0e0;
          }
        `}
`;

const PrimaryButton = styled(Button)`
  background-color: #006699;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: bold;
  text-transform: uppercase;
  height: 3.8rem;
  transition: background-color 0.2s;

  svg {
    display: block;
    fill: #fff;
  }

  ${(p) =>
    p.disabled
      ? css`
          background-color: darkgrey;
        `
      : css`
          &:hover {
            background-color: #0170a7;
          }
        `}
`;

const FinishButton = styled(Button)`
  background-color: #53a752;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: bold;
  text-transform: uppercase;
  height: 3.8rem;
  transition: background-color 0.2s;

  svg {
    display: block;
    fill: #fff;
  }

  &:hover {
    background-color: #5db95c;
  }
`;

interface Props {
  onFinish?: () => void;
  isFinished?: boolean;
  disableSubmit?: boolean;
  disablePause?: boolean;
}

const Header: React.FC<Props> = (props) => {
  const [state, dispatch] = useContext(ExamContext);

  const isFinished = props.isFinished || state.isCompleted;

  if (!state.exam) {
    console.error("No exam in state while rendering header");
    return null;
  }
  const exam = state.exam;

  const currentItemNumber = getItemCountBefore(exam, state.position) + 1;
  const itemCount = getItemCount(exam);
  const itemCountInSection = getItemCountInSection(exam, state.position);

  let canBack = false;
  let canNext = false;
  let submitMode = "submit-exam";

  if (state.itemNavigationMode === "linear") {
    // todo
    console.error("Linear item navigation not implemented");
  } else {
    if (state.sectionNavigationMode === "linear") {
      canBack = state.position.question > 0;
      canNext = state.position.question < itemCountInSection - 1;

      submitMode =
        currentItemNumber === itemCount ? "submit-exam" : "submit-section";
    } else {
      canBack = currentItemNumber > 1;
      canNext = currentItemNumber < itemCount;
    }
  }

  const showSubmit = !canNext && !props.disableSubmit;

  const handleSubmit = async () => {
    if (submitMode === "submit-exam") {
      props.onFinish?.();
    } else if (submitMode === "submit-section") {
      const shouldSubmit = await ConfirmModal.show({
        title: "Submit section",
        children: (
          <Modal.PaddedBody>
            Are you sure you want to submit this section? You will not be able
            to return to this section.
          </Modal.PaddedBody>
        ),
      });

      if (!shouldSubmit) return;

      dispatch({
        type: "SET_POSITION",
        position: {
          form: 0,
          section: state.position.section + 1,
          question: 0,
        },
      });
    }
  };

  return (
    <ExamHeader>
      <ExamHeaderMain>
        <ExamHeaderTitle>
          <ExamTitle>{state.exam?.name}</ExamTitle>
        </ExamHeaderTitle>
        {!isFinished && (state.windowSize?.width || 0) > 500 && (
          <ExamHeaderStatus>
            {currentItemNumber} of {itemCount}
          </ExamHeaderStatus>
        )}
        {!isFinished && (
          <ExamHeaderButtons>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {!props.disablePause && (
                <SecondaryButton
                  onClick={() =>
                    dispatch({
                      type: "SET_PAUSE",
                      isPaused: true,
                    })
                  }
                >
                  <svg height="24" viewBox="0 0 24 24" width="24">
                    <path d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path>
                  </svg>
                </SecondaryButton>
              )}
              <SecondaryButton
                onClick={() =>
                  dispatch({
                    type: "BACK",
                  })
                }
                disabled={!canBack}
              >
                <svg height="24" viewBox="0 0 24 24" width="24">
                  <path d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"></path>
                </svg>
                {(state.windowSize?.width || 0) > 500 && <span>Back</span>}
              </SecondaryButton>

              {(canNext || !showSubmit) && (
                //
                // Show always if !showSubmit, so we don't have buttons jumping around
                //
                <PrimaryButton
                  onClick={() =>
                    dispatch({
                      type: "NEXT",
                    })
                  }
                  disabled={!canNext}
                >
                  <span>Next</span>
                  <svg height="24" viewBox="0 0 24 24" width="24">
                    <path d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"></path>
                  </svg>
                </PrimaryButton>
              )}

              {showSubmit && (
                <FinishButton onClick={handleSubmit}>
                  <svg
                    height="18"
                    viewBox="0 0 24 24"
                    style={{ marginRight: "0.3rem" }}
                  >
                    <g>
                      <path d="m9.707 19.121c-.187.188-.442.293-.707.293s-.52-.105-.707-.293l-5.646-5.647c-.586-.586-.586-1.536 0-2.121l.707-.707c.586-.586 1.535-.586 2.121 0l3.525 3.525 9.525-9.525c.586-.586 1.536-.586 2.121 0l.707.707c.586.586.586 1.536 0 2.121z"></path>
                    </g>
                  </svg>
                  <span>Finish</span>
                </FinishButton>
              )}
            </div>
          </ExamHeaderButtons>
        )}
      </ExamHeaderMain>
      <ExamHeaderProgress>
        <ProgressBar current={currentItemNumber} total={itemCount} />
      </ExamHeaderProgress>
    </ExamHeader>
  );
};

export default Header;
